import React, { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const Terms: React.FC = () => {
  useEffect(() => {
    // SEO: Title
    document.title = "Terms & Conditions — ai4hack";

    // SEO: Meta description
    const metaDesc =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
        return m;
      })();
    metaDesc.setAttribute(
      "content",
      "Terms & Conditions for the ai4hack AI Hackathon. Review eligibility, submissions, IP, prizes, conduct, and more. Last updated August 11, 2025."
    );

    // SEO: Canonical
    const canonicalHref = "https://ai4hack.com/terms";
    const existingCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (existingCanonical) {
      existingCanonical.href = canonicalHref;
    } else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonicalHref);
      document.head.appendChild(link);
    }
  }, []);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ai4hack.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Terms & Conditions",
        item: "https://ai4hack.com/terms",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-beige text-foreground">
      <Navbar />

      <main id="main-content" className="relative pt-28 pb-16 overflow-x-hidden">
        <div className="pointer-events-none absolute -top-10 -right-10 w-1/2 h-[40%] opacity-20 blur-3xl rounded-full" style={{ background: 'linear-gradient(180deg, rgba(242,76,39,0.8) 0%, rgba(242,76,39,0) 100%)' }}></div>
        <section className="section-container">
          <article className="mx-auto max-w-3xl">
            <header className="mb-8 text-center md:text-left">
              <div className="flex justify-center md:justify-start mb-3">
                <div
                  className="inline-flex items-center justify-center px-4 py-2 rounded-full"
                  style={{
                    backgroundColor: 'rgba(242, 76, 39, 0.1)',
                    border: '1px solid rgba(242, 76, 39, 0.2)',
                    color: 'rgba(242, 76, 39, 1)',
                  }}
                >
              <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white mr-2 text-[10px] font-semibold"
                    style={{ backgroundColor: 'rgba(242, 76, 39, 1)' }}
                  >
                    10
                  </span>
                  <span>Terms &amp; Conditions</span>
                </div>
              </div>
              <h1
                className="text-3xl sm:text-4xl md:text-5xl font-display font-bold tracking-tight"
                style={{
                  background: 'linear-gradient(135deg, rgba(242, 76, 39, 1) 0%, rgba(31, 31, 30, 1) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                Terms &amp; Conditions — ai<span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span>hack
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">Last Updated: August 11, 2025</p>
            </header>

            <hr className="my-8 border-border" />

            <section className="space-y-6">
              <p>
                Welcome to ai4hack.com (“Site”) — the official website of the ai4hack AI Hackathon (“Event”), organized by ai4hack (“we”, “our”, “us”). By registering for and participating in the Event, you agree to the following terms and conditions.
              </p>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">1. Eligibility</h2>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>The Event is open to individuals who are 18 years or older (or have parental/guardian consent if under 18).</li>
                  <li>You must register through our official website and provide accurate, up-to-date information.</li>
                  <li>We reserve the right to accept or reject any application.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">2. Registration &amp; Participation</h2>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>You agree to participate in good faith, follow Event guidelines, and respect other participants, mentors, and jurors.</li>
                  <li>You are responsible for your own equipment, internet connection, and tools used during the Event.</li>
                  <li>Any behavior that is discriminatory, harassing, or disruptive may result in immediate disqualification.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">3. Project Submissions</h2>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>All projects must be created entirely during the Event timeframe. Pre-existing work must be clearly disclosed.</li>
                  <li>You must have the rights to all materials used in your project (code, datasets, images, music, etc.).</li>
                  <li>Submissions must not contain illegal, harmful, or offensive content.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">4. Intellectual Property</h2>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>You (or your team) retain ownership of your project’s intellectual property.</li>
                  <li>By submitting your project, you grant ai4hack and its partners a non-exclusive, royalty-free license to display, promote, and showcase your submission in connection with the Event.</li>
                  <li>Sponsors or partners may propose collaborations or investment opportunities, but any agreements are separate from these Terms.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">5. Prizes</h2>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>Prizes, if offered, will be announced on the Event website and are subject to eligibility verification.</li>
                  <li>We reserve the right to modify, replace, or cancel any prize if necessary.</li>
                  <li>Winners are responsible for any taxes or fees related to their prize.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">6. Code of Conduct</h2>
                <p className="mt-4">Participants must:</p>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>Treat all individuals with respect.</li>
                  <li>Not plagiarize or misrepresent work.</li>
                  <li>Avoid malicious or unethical uses of AI (e.g., deepfakes without consent, harmful misinformation).</li>
                </ul>
                <p className="mt-3">Violation of the Code of Conduct may result in disqualification.</p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">7. Liability</h2>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>The Event is provided “as is” without warranties of any kind.</li>
                  <li>We are not liable for any loss, damage, or injury resulting from participation in the Event.</li>
                  <li>You are responsible for backing up your work — we are not responsible for lost data.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">8. Changes &amp; Cancellation</h2>
                <ul className="mt-3 list-disc pl-6 space-y-2">
                  <li>We reserve the right to modify the Event schedule, rules, or format at any time.</li>
                  <li>In case of cancellation, we will make reasonable efforts to notify participants.</li>
                </ul>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">9. Governing Law</h2>
                <p className="mt-4">These Terms are governed by and construed in accordance with the laws of Delaware, United States, without regard to conflict of law principles.</p>
              </section>

              <hr className="my-6 border-border" />

              <section>
                <h2 className="text-xl font-medium">10. Contact Us</h2>
                <p className="mt-4">If you have questions about these Terms &amp; Conditions, contact us at:</p>
          <ul className="mt-3 list-none pl-0 space-y-1">
            <li>📍 730 Moreno Ave, Palo Alto, CA, 94303, US</li>
            <li>📧 <a className="text-primary underline" href="mailto:hi@ai4hack.com">hi@ai4hack.com</a></li>
            <li>🌍 <a className="text-primary underline" href="https://ai4hack.com" target="_blank" rel="noopener noreferrer">ai4hack.com</a></li>
          </ul>
              </section>
            </section>
          </article>
        </section>
      </main>

      {/* Structured data */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <Footer />
    </div>
  );
};

export default Terms;
